import { img } from "../images";
import type { Blog } from "./types";

const DR_VANITHA = "Dr. M. Vanitha";
const VANITHA_BIO =
  "Dr. M. Vanitha is a Dermatologist at AV Clinic, Krishnagiri (M.B.B.S., D.D.V.L.). She provides specialist care for a wide range of skin, hair and nail conditions — from acne and pigmentation to psoriasis, eczema, fungal infections and hair fall — combining accurate diagnosis with practical, personalised treatment plans.";
const VANITHA_ROLE = "Dermatologist at AV Clinic, Krishnagiri";

export const aestheticBlogs: Blog[] = [
  {
    slug: "chemical-peels",
    title: "Chemical Peels: Benefits and Considerations",
    category: "Aesthetic Dermatology",
    excerpt:
      "Chemical peels can improve skin tone and texture, but the right one matters. Learn the benefits and considerations.",
    image: img.svcChemicalPeels,
    imageAlt: "Chemical peels benefits and considerations",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-08-27",
    modified: "2026-08-27",
    readTime: "5 min read",
    tags: ["chemical peel", "skin peels", "skin treatment", "aesthetic"],
    primaryKeyword: "chemical peel",
    secondaryKeywords: ["chemical peel benefits", "skin peel Krishnagiri", "facial peel"],
    relatedService: "chemical-peels",
    relatedServiceLabel: "Chemical Peel Treatment",
    relatedArticles: ["skin-rejuvenation", "uneven-skin-tone", "choose-skin-treatment"],
    sections: [
      {
        heading: "What a chemical peel is",
        body: [
          "A chemical peel uses a solution to remove the outer layer of the skin, encouraging new, smoother skin to form. It can improve tone, texture, pigmentation and the look of fine lines.",
        ],
      },
      {
        heading: "Potential benefits",
        body: ["Depending on the type of peel, benefits may include:"],
        list: [
          "Brighter, more even skin tone",
          "Smoother texture",
          "Reduction in the appearance of mild pigmentation",
          "Softer look of fine lines",
        ],
      },
      {
        heading: "Considerations",
        body: [
          "The strength and type of peel must be matched to your skin. A dermatologist assesses your skin, history and goals before recommending a peel, and gives clear after-care advice.",
        ],
      },
      {
        heading: "Results take time",
        body: [
          "Improvement is usually gradual, and several sessions may be needed. Sun protection is essential during and after treatment.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is a chemical peel safe for all skin types?",
        a: "Not all peels suit every skin type. A dermatologist matches the peel to your skin for safety and best results.",
      },
      {
        q: "How much downtime is needed?",
        a: "This depends on the peel's strength. A dermatologist will explain what to expect for your treatment.",
      },
    ],
    seo: {
      title: "Chemical Peels: Benefits & Considerations | AV Clinic",
      description:
        "Learn about chemical peels — their benefits, how they are matched to your skin and what to expect. Aesthetic dermatology at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "laser-skin-treatments",
    title: "Laser Skin Treatments Explained",
    category: "Aesthetic Dermatology",
    excerpt:
      "Laser technology can address pigmentation, texture and more. Learn how laser skin treatments work and what to expect.",
    image: img.svcAdvancedProcedures,
    imageAlt: "Laser skin treatments explained",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-08-23",
    modified: "2026-08-23",
    readTime: "5 min read",
    tags: ["laser skin treatment", "skin laser", "aesthetic", "skin rejuvenation"],
    primaryKeyword: "laser skin treatment",
    secondaryKeywords: ["laser skin treatment Krishnagiri", "laser for skin", "skin laser"],
    relatedService: "advanced-skin-procedures",
    relatedServiceLabel: "Advanced Skin Procedures",
    relatedArticles: ["skin-rejuvenation", "chemical-peels", "anti-ageing-skincare"],
    sections: [
      {
        heading: "What laser treatments do",
        body: [
          "Laser skin treatments use focused light to target specific skin concerns, such as pigmentation, texture, or signs of sun damage. Different lasers are suited to different concerns and skin types.",
        ],
      },
      {
        heading: "Common uses",
        body: ["Laser treatments are commonly used for:"],
        list: [
          "Pigmentation and dark spots",
          "Uneven skin texture and tone",
          "Fine lines",
          "Scars and sun damage",
        ],
      },
      {
        heading: "What to expect",
        body: [
          "A consultation is key — a specialist assesses your skin and decides whether laser is suitable. The number of sessions varies, and sun protection before and after is essential.",
        ],
      },
      {
        heading: "Realistic expectations",
        body: [
          "Results are gradual and depend on your skin and the concern. A dermatologist will explain what is realistic and how to plan treatment safely.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is laser skin treatment suitable for everyone?",
        a: "Not everyone is a candidate. Your skin type and concern determine suitability, which a specialist assesses at consultation.",
      },
      {
        q: "Is laser treatment painful?",
        a: "Comfort varies. Many people feel mild warmth during treatment, and a specialist will discuss comfort measures.",
      },
    ],
    seo: {
      title: "Laser Skin Treatments Explained | AV Clinic",
      description:
        "Learn how laser skin treatments work in Krishnagiri — for pigmentation, texture and sun damage. Aesthetic dermatology at AV Clinic.",
    },
  },
  {
    slug: "laser-hair-reduction",
    title: "Laser Hair Reduction: What to Know",
    category: "Aesthetic Dermatology",
    excerpt:
      "Laser hair reduction is a popular option for long-term hair management. Learn what it involves and what to expect.",
    image: img.svcLaserHairReduction,
    imageAlt: "Laser hair reduction what to know",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-08-19",
    modified: "2026-08-19",
    readTime: "5 min read",
    popular: true,
    tags: ["laser hair reduction", "hair removal", "aesthetic", "skin care"],
    primaryKeyword: "laser hair reduction",
    secondaryKeywords: ["laser hair reduction Krishnagiri", "laser hair removal", "permanent hair reduction"],
    relatedService: "laser-hair-reduction",
    relatedServiceLabel: "Laser Hair Reduction",
    relatedArticles: ["skin-rejuvenation", "choose-skin-treatment", "hair-care-guide"],
    sections: [
      {
        heading: "How laser hair reduction works",
        body: [
          "Laser hair reduction uses focused light energy to target the hair follicle, reducing hair growth over time. It is a popular option for long-term hair management on the face and body.",
        ],
      },
      {
        heading: "What to expect",
        body: ["Understanding the process helps you plan:"],
        list: [
          "Multiple sessions are needed for lasting results",
          "The number of sessions depends on the area and your hair type",
          "Results vary, and some reduction rather than complete removal is typical",
          "Sun protection after treatment is important",
        ],
      },
      {
        heading: "Suitability",
        body: [
          "Laser effectiveness can vary with skin and hair colour. A specialist assesses your skin and hair to confirm whether laser hair reduction is suitable and how to plan it.",
        ],
      },
      {
        heading: "A specialist first step",
        body: [
          "A dermatology consultation helps you understand what is realistic and how to prepare. It is the safest way to plan laser hair reduction.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is laser hair reduction permanent?",
        a: "It significantly reduces hair growth, but some regrowth can occur. Maintenance sessions may be needed.",
      },
      {
        q: "How many sessions do I need?",
        a: "Typically several sessions are needed, and the number depends on the area and your hair type.",
      },
    ],
    seo: {
      title: "Laser Hair Reduction: What to Know | AV Clinic",
      description:
        "Learn about laser hair reduction in Krishnagiri — how it works, sessions and expectations. Aesthetic dermatology at AV Clinic.",
    },
  },
  {
    slug: "skin-rejuvenation",
    title: "Skin Rejuvenation Treatments",
    category: "Aesthetic Dermatology",
    excerpt:
      "Skin rejuvenation helps refresh dull, tired skin. Learn the treatment options and what they can achieve.",
    image: img.svcSkinRejuvenation,
    imageAlt: "Skin rejuvenation treatments",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-08-15",
    modified: "2026-08-15",
    readTime: "5 min read",
    tags: ["skin rejuvenation", "skin care", "aesthetic", "glowing skin"],
    primaryKeyword: "skin rejuvenation",
    secondaryKeywords: ["skin rejuvenation Krishnagiri", "skin glow treatment", "facial rejuvenation"],
    relatedService: "skin-rejuvenation",
    relatedServiceLabel: "Skin Rejuvenation",
    relatedArticles: ["chemical-peels", "laser-skin-treatments", "facial-skin-care"],
    sections: [
      {
        heading: "What skin rejuvenation means",
        body: [
          "Skin rejuvenation refers to treatments that refresh the skin, improving radiance, tone and texture. It can help address the effects of ageing, sun damage and dullness.",
        ],
      },
      {
        heading: "Treatment options",
        body: ["Options may include:"],
        list: [
          "Chemical peels",
          "Laser and light-based treatments",
          "Professional facials and skin-care procedures",
          "Medically guided skin-care routines",
        ],
      },
      {
        heading: "Choosing a treatment",
        body: [
          "The right treatment depends on your skin type, concerns and goals. A dermatologist recommends an approach suited to you rather than a generic treatment.",
        ],
      },
      {
        heading: "Daily support",
        body: [
          "Treatments work best alongside a good daily routine — gentle cleansing, moisturising and daily sunscreen. This supports and prolongs results.",
        ],
      },
    ],
    faqs: [
      {
        q: "How soon do skin rejuvenation results show?",
        a: "This varies by treatment. Some show early improvement, while others need a few sessions for results.",
      },
      {
        q: "Is skin rejuvenation only for older skin?",
        a: "No. Skin rejuvenation can help at any age to refresh tone and texture, tailored to your needs.",
      },
    ],
    seo: {
      title: "Skin Rejuvenation Treatments | AV Clinic",
      description:
        "Learn about skin rejuvenation treatments in Krishnagiri — peels, laser and professional care. Aesthetic dermatology at AV Clinic.",
    },
  },
  {
    slug: "uneven-skin-tone",
    title: "Treatments for Uneven Skin Tone",
    category: "Aesthetic Dermatology",
    excerpt:
      "Uneven skin tone is common and can be improved. Learn the treatments that help even out your complexion.",
    image: img.svcSkinBrightening,
    imageAlt: "Treatments for uneven skin tone",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-08-11",
    modified: "2026-08-11",
    readTime: "5 min read",
    tags: ["uneven skin tone", "skin brightening", "pigmentation", "aesthetic"],
    primaryKeyword: "uneven skin tone treatment",
    secondaryKeywords: ["even skin tone", "skin brightening Krishnagiri", "dull skin"],
    relatedService: "skin-brightening",
    relatedServiceLabel: "Skin Brightening",
    relatedArticles: ["pigmentation-causes", "hyperpigmentation-treatment", "skin-brightening"],
    sections: [
      {
        heading: "Why tone becomes uneven",
        body: [
          "Uneven skin tone usually results from pigmentation, sun exposure, past inflammation or dryness. It can make the complexion look dull or patchy.",
        ],
      },
      {
        heading: "Treatment options",
        body: ["Working with a dermatologist, options may include:"],
        list: [
          "Brightening topical products",
          "Chemical peels",
          "Laser or light-based treatments",
          "Sun protection to prevent worsening",
        ],
      },
      {
        heading: "The role of sun protection",
        body: [
          "Daily sunscreen is the most important step. Without it, even the best treatment is undone by sun exposure.",
        ],
      },
      {
        heading: "A tailored plan",
        body: [
          "Because skin types differ, a personalised plan is safer and more effective. A dermatologist can match treatments to your skin and goals.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can uneven skin tone be completely fixed?",
        a: "It can usually be significantly improved, though some may require maintenance, especially with sun exposure.",
      },
      {
        q: "How long until I see results?",
        a: "Results are gradual, often over several weeks to months, depending on the treatment.",
      },
    ],
    seo: {
      title: "Treatments for Uneven Skin Tone | AV Clinic",
      description:
        "Learn about treatments for uneven skin tone — brightening, peels and laser — plus the importance of sunscreen. Aesthetic care at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "facial-skin-care",
    title: "Facial Skin Care Treatments",
    category: "Skin Care",
    excerpt:
      "Professional facial treatments can refresh and support your skin. Learn what is available and how to choose.",
    image: img.svcSkinRejuvenation,
    imageAlt: "Facial skin care treatments",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-08-07",
    modified: "2026-08-07",
    readTime: "4 min read",
    tags: ["facial", "skin care", "facial treatment", "aesthetic"],
    primaryKeyword: "facial skin care",
    secondaryKeywords: ["facial treatment Krishnagiri", "facial for skin", "skin care facial"],
    relatedService: "cosmetic-dermatology",
    relatedServiceLabel: "Cosmetic Dermatology",
    relatedArticles: ["skin-rejuvenation", "chemical-peels", "sensitive-skin-care"],
    sections: [
      {
        heading: "Facials can support your skin",
        body: [
          "Professional facial treatments cleanse, exfoliate and hydrate the skin, supporting a clear, healthy complexion. They are a helpful complement to a good daily routine.",
        ],
      },
      {
        heading: "Common treatments",
        body: ["Facial treatments may include:"],
        list: [
          "Deep cleansing and exfoliation",
          "Hydrating and brightening treatments",
          "Chemical peels",
          "Medically guided facials for specific concerns",
        ],
      },
      {
        heading: "Choosing the right facial",
        body: [
          "The best facial depends on your skin type and concern. A dermatologist can recommend what is suitable and safe, avoiding over-treatment that can irritate skin.",
        ],
      },
      {
        heading: "It works best with a routine",
        body: [
          "Facials complement — not replace — daily cleansing, moisturising and sunscreen. Consistency is key for long-term skin health.",
        ],
      },
    ],
    faqs: [
      {
        q: "How often should I get a facial?",
        a: "This depends on your skin and the treatment. A dermatologist can recommend a suitable frequency.",
      },
      {
        q: "Are facials safe for sensitive skin?",
        a: "Gentle facials can be suitable, but sensitive skin needs proper matching. It is safest to get guidance first.",
      },
    ],
    seo: {
      title: "Facial Skin Care Treatments | AV Clinic",
      description:
        "Learn about professional facial skin care treatments in Krishnagiri and how to choose the right one. Aesthetic care at AV Clinic.",
    },
  },
  {
    slug: "anti-ageing-skincare",
    title: "Anti-Aging Skin Care Guide",
    category: "Aesthetic Dermatology",
    excerpt:
      "A well-chosen routine can help keep skin looking youthful. Learn the essentials of an anti-ageing skin-care plan.",
    image: img.svcAntiAgeing,
    imageAlt: "Anti-ageing skin care guide",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-08-03",
    modified: "2026-08-03",
    readTime: "5 min read",
    tags: ["anti-ageing", "skin care", "wrinkles", "aesthetic"],
    primaryKeyword: "anti-ageing skin care",
    secondaryKeywords: ["anti ageing routine", "skin ageing", "youthful skin Krishnagiri"],
    relatedService: "anti-ageing",
    relatedServiceLabel: "Anti-Ageing Treatment",
    relatedArticles: ["fine-lines-wrinkles", "sun-damage-skin", "skin-rejuvenation"],
    sections: [
      {
        heading: "Ageing skin is natural",
        body: [
          "Skin naturally changes with age — losing firmness and becoming more prone to fine lines and dryness. Good skin care can support a healthy, youthful appearance.",
        ],
      },
      {
        heading: "Core routine",
        body: ["An effective anti-ageing routine includes:"],
        list: [
          "Daily sunscreen, the single most important step",
          "Gentle cleansing and moisturising",
          "Products with proven ingredients, chosen appropriately",
          "Adequate hydration and sleep",
        ],
      },
      {
        heading: "Sunscreen is essential",
        body: [
          "Sun exposure is a leading cause of skin ageing. Daily broad-spectrum sunscreen, reapplied, protects skin and supports every other anti-ageing effort.",
        ],
      },
      {
        heading: "When to seek a specialist",
        body: [
          "For more noticeable signs of ageing, a dermatologist can recommend professional treatments suited to your skin and goals.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can anti-ageing skin care reverse wrinkles?",
        a: "Good care and appropriate treatments can improve the look of skin and fine lines, but they cannot fully reverse deeper wrinkles.",
      },
      {
        q: "When should I start anti-ageing skin care?",
        a: "Starting good habits — especially sunscreen — early in your 20s and 30s is helpful. It is never too late to start.",
      },
    ],
    seo: {
      title: "Anti-Aging Skin Care Guide | AV Clinic",
      description:
        "Learn the essentials of anti-ageing skin care in Krishnagiri — sunscreen, routine and professional treatments. Aesthetic dermatology at AV Clinic.",
    },
  },
  {
    slug: "fine-lines-wrinkles",
    title: "Fine Lines and Wrinkles: Treatment Options",
    category: "Aesthetic Dermatology",
    excerpt:
      "Fine lines and wrinkles are a natural part of ageing. Learn the options to soften their appearance.",
    image: img.svcAntiAgeing,
    imageAlt: "Fine lines and wrinkles treatment options",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-07-30",
    modified: "2026-07-30",
    readTime: "5 min read",
    tags: ["wrinkles", "fine lines", "anti-ageing", "aesthetic"],
    primaryKeyword: "wrinkle treatment",
    secondaryKeywords: ["fine lines treatment", "anti-wrinkle", "wrinkle Krishnagiri"],
    relatedService: "anti-ageing",
    relatedServiceLabel: "Anti-Ageing Treatment",
    relatedArticles: ["anti-ageing-skincare", "sun-damage-skin", "skin-rejuvenation"],
    sections: [
      {
        heading: "Why lines form",
        body: [
          "Fine lines and wrinkles develop as skin loses elasticity and collagen with age, and as it is affected by sun exposure, facial expression and lifestyle. They are a normal part of ageing.",
        ],
      },
      {
        heading: "Options to soften them",
        body: ["Depending on your skin, options may include:"],
        list: [
          "Dermatologist-guided skin-care products",
          "Chemical peels",
          "Laser or light-based treatments",
          "Professional anti-ageing procedures",
        ],
      },
      {
        heading: "Start with prevention",
        body: [
          "Daily sunscreen, adequate hydration, good sleep and avoiding smoking are the best preventive steps. These support skin health and help slow further lines.",
        ],
      },
      {
        heading: "A tailored approach",
        body: [
          "The right treatment depends on your skin type, the depth of lines and your goals. A dermatologist recommends a plan that is safe and suits you.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can wrinkles be removed completely?",
        a: "Deep wrinkles can usually be softened but not completely removed. A specialist can explain what is realistic.",
      },
      {
        q: "Does sunscreen prevent wrinkles?",
        a: "Yes. Sunscreen is one of the most effective ways to help prevent and reduce the appearance of wrinkles.",
      },
    ],
    seo: {
      title: "Fine Lines & Wrinkles Treatment | AV Clinic",
      description:
        "Learn about treatment options for fine lines and wrinkles in Krishnagiri — prevention, peels and laser. Aesthetic dermatology at AV Clinic.",
    },
  },
  {
    slug: "sun-damage-skin",
    title: "Sun Damage and Skin Treatment",
    category: "Aesthetic Dermatology",
    excerpt:
      "Sun exposure is a major cause of pigmentation, ageing and skin damage. Learn how to protect skin and treat damage.",
    image: img.svcSkinRejuvenation,
    imageAlt: "Sun damage and skin treatment",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-07-26",
    modified: "2026-07-26",
    readTime: "5 min read",
    tags: ["sun damage", "sunspots", "skin care", "aesthetic"],
    primaryKeyword: "sun damage skin treatment",
    secondaryKeywords: ["sunspots treatment", "sun damage skin", "hyperpigmentation sun"],
    relatedService: "skin-rejuvenation",
    relatedServiceLabel: "Skin Rejuvenation",
    relatedArticles: ["pigmentation-causes", "anti-ageing-skincare", "uneven-skin-tone"],
    sections: [
      {
        heading: "UV damage adds up",
        body: [
          "Ultraviolet (UV) exposure is a major driver of skin ageing, pigmentation and skin damage. The effects accumulate over years, which is why sun protection matters at every age.",
        ],
      },
      {
        heading: "Common signs",
        body: ["Signs of sun damage can include:"],
        list: [
          "Dark spots and uneven pigmentation",
          "Fine lines and wrinkles",
          "Rough or dry texture",
          "Dull-looking skin",
        ],
      },
      {
        heading: "Protection is the foundation",
        body: [
          "Daily broad-spectrum sunscreen, worn on exposed skin and reapplied, plus staying out of peak sun, is the most effective protection. Hats and shade also help.",
        ],
      },
      {
        heading: "Treating existing damage",
        body: [
          "Existing sunspots and damage can often be improved with dermatologist-guided treatments such as peels or laser. A dermatologist can assess your skin and recommend a suitable plan.",
        ],
      },
    ],
    faqs: [
      {
        q: "How often should I apply sunscreen?",
        a: "Apply a generous amount daily and reapply every two to three hours, especially with sun exposure.",
      },
      {
        q: "Can sun damage be reversed?",
        a: "Some signs can be improved with treatment and protection, though complete reversal is not always possible. A specialist can advise.",
      },
    ],
    seo: {
      title: "Sun Damage & Skin Treatment | AV Clinic",
      description:
        "Learn how to protect skin from sun damage and treat existing sunspots and ageing. Dermatology care at AV Clinic, Krishnagiri.",
    },
  },
  {
    slug: "choose-skin-treatment",
    title: "How to Choose the Right Skin Treatment",
    category: "Aesthetic Dermatology",
    excerpt:
      "With so many options, choosing a skin treatment can feel confusing. Learn how to pick the right one safely.",
    image: img.svcCosmeticDerma,
    imageAlt: "How to choose the right skin treatment",
    author: DR_VANITHA,
    authorBio: VANITHA_BIO,
    authorRole: VANITHA_ROLE,
    date: "2026-07-22",
    modified: "2026-07-22",
    readTime: "5 min read",
    tags: ["skin treatment", "choose treatment", "aesthetic", "dermatology"],
    primaryKeyword: "choose the right skin treatment",
    secondaryKeywords: ["skin treatment Krishnagiri", "how to choose skin treatment", "skin care advice"],
    relatedService: "cosmetic-dermatology",
    relatedServiceLabel: "Cosmetic Dermatology",
    relatedArticles: ["skin-rejuvenation", "chemical-peels", "laser-skin-treatments"],
    sections: [
      {
        heading: "One size does not fit all",
        body: [
          "The right skin treatment depends on your skin type, your specific concern and your goals. What works for someone else may not suit you.",
        ],
      },
      {
        heading: "How to decide",
        body: ["A good approach considers:"],
        list: [
          "Your skin type and sensitivity",
          "The specific concern you want to address",
          "Realistic expectations and time commitment",
          "Safety and suitability of the treatment",
        ],
      },
      {
        heading: "The value of professional guidance",
        body: [
          "Self-selecting treatments, especially strong ones, can irritate or damage skin. A dermatologist assesses your skin first and recommends a safe, suitable plan.",
        ],
      },
      {
        heading: "Ask the right questions",
        body: [
          "Before any treatment, ask about the expected results, number of sessions, downtime and after-care. This helps you decide with confidence.",
        ],
      },
    ],
    faqs: [
      {
        q: "How do I know which skin treatment is right for me?",
        a: "A dermatology consultation is the best way. The specialist assesses your skin and recommends an appropriate treatment.",
      },
      {
        q: "Should I avoid strong skin treatments without advice?",
        a: "Yes. Strong treatments without guidance can irritate or harm skin. Seek professional advice first.",
      },
    ],
    seo: {
      title: "How to Choose the Right Skin Treatment | AV Clinic",
      description:
        "Learn how to choose the right skin treatment in Krishnagiri — skin type, goals and professional guidance. Aesthetic dermatology at AV Clinic.",
    },
  },
];